import { useEffect, useState, useCallback } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  TextField,
} from "@mui/material"

import Loading from "../../Loading"
import { getSession } from "../../../auth"
import api from "../../../api"
import useNotification from "../../Notification"

export default function Notifications({ handleClose, originNumbers, calendarNotification, setCalendarNotification }) {
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(false)

  const [state, setState] = useState(
    (calendarNotification !== null)
      ? calendarNotification
      : {
        id: null,
      	origin_number: "",
        on_create_template: "",
        on_update_template: "",
        on_delete_template: "",
        first_reminder_template: "",
        first_reminder_offset: 24,
        second_reminder_template: "",
        second_reminder_offset: 10,
      }
  )

  const countTemplateParams = (template) => {
    const body = template.components?.find(c => c.type === 'BODY')
    return (body?.text?.match(/{{/g) || []).length
  }

  const filterTemplates = (paramCount) => (template) =>
    template.status === "APPROVED" &&
    template.language === "pt_BR" &&
    !template.name.includes("sample_") &&
    countTemplateParams(template) === paramCount

  const fetchTemplates = useCallback(async () => {
    const enterpriseId = getSession().profile.enterprise.id
    const response = await api.get(`/api/enterprises/${enterpriseId}/templates/`, { timeout: 0 })
    if (response.status === 200)
      setTemplates(response.data)
  }, [setTemplates])

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      await Promise.all([
        fetchTemplates()
      ])
      setLoading(false)
    }
    fetchData()
  }, [])

  const handleSave = async () => {
    const enterpriseId = getSession().profile?.enterprise?.id
    setLoading(true)
    const response = await api.post(`api/enterprises/${enterpriseId}/calendar_notification/`, state)
    setLoading(false)
    if (response.status !== 200) {
      useNotification("Ops", "Algo deu errado, tente novamente.", "danger")
      return
    }
    setCalendarNotification({ ...state })
    useNotification("Sucesso", "Configurações salvas com sucesso.", "success")
    handleClose()
  }

  return (
    <>
      <Loading loading={loading} />
      <Dialog open={true} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Configurar Notificações WhatsApp</DialogTitle>
        <Divider />
        <DialogContent>
          <Box className="category-title">
            Número de origem
          </Box>
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>Número</InputLabel>
            <Select
              value={state.origin_number}
              label="Número"
              onChange={(event) => setState((prevState) => ({ ...prevState, origin_number: event.target.value }))}
            >
              {originNumbers.map((number) => (
                <MenuItem key={number} value={number}>
                  {number}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box className="category-title push-top" display="flex" justifyContent="space-between">
            Novo evento
            <Button
              sx={{ marginBottom: "-.75rem" }}
              onClick={() => setState((prev) => ({
                ...prev,
                on_create_template: null
              }))}>Desativar</Button>
          </Box>
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>Template</InputLabel>
            <Select
              value={state.on_create_template}
              label="Template"
              onChange={(e) =>
                setState((prev) => ({
                  ...prev,
                  on_create_template: e.target.value
                }))
              }
              disabled={!state.origin_number}
            >
              {templates[state.origin_number]?.waba_templates?.filter(filterTemplates(3)).map((tpl) => (
                <MenuItem key={tpl.name} value={tpl.name}>
                  {tpl.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box className="category-title push-top" display="flex" justifyContent="space-between">
            Atualização
            <Button
              sx={{ marginBottom: "-.75rem" }}
              onClick={() => setState((prev) => ({
                ...prev,
                on_update_template: null
              }))}>Desativar</Button>
          </Box>
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>Template</InputLabel>
            <Select
              value={state.on_update_template}
              label="Template"
              onChange={(e) =>
                setState((prev) => ({
                  ...prev,
                  on_update_template: e.target.value
                }))
              }
              disabled={!state.origin_number}
            >
              {templates[state.origin_number]?.waba_templates?.filter(filterTemplates(2)).map((tpl) => (
                <MenuItem key={tpl.name} value={tpl.name}>
                  {tpl.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box className="category-title push-top" display="flex" justifyContent="space-between">
            Cancelamento
            <Button
              sx={{ marginBottom: "-.75rem" }}
              onClick={() => setState((prev) => ({
                ...prev,
                on_delete_template: null
              }))}>Desativar</Button>
          </Box>
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>Template</InputLabel>
            <Select
              value={state.on_delete_template}
              label="Template"
              onChange={(e) =>
                setState((prev) => ({
                  ...prev,
                  on_delete_template: e.target.value
                }))
              }
              disabled={!state.origin_number}
            >
              {templates[state.origin_number]?.waba_templates?.filter(filterTemplates(2)).map((tpl) => (
                <MenuItem key={tpl.name} value={tpl.name}>
                  {tpl.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box className="category-title push-top" display="flex" justifyContent="space-between">
            Primeiro lembrete
            <Button
              sx={{ marginBottom: "-.75rem" }}
              onClick={() => setState((prev) => ({
                ...prev,
                first_reminder_template: null
              }))}>Desativar</Button>
          </Box>
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>Template</InputLabel>
            <Select
              value={state.first_reminder_template}
              label="Template"
              onChange={(e) =>
                setState((prev) => ({
                  ...prev,
                  first_reminder_template: e.target.value
                }))
              }
              disabled={!state.origin_number}
            >
              {templates[state.origin_number]?.waba_templates?.filter(filterTemplates(3)).map((tpl) => (
                <MenuItem key={tpl.name} value={tpl.name}>
                  {tpl.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Horas antes do evento"
            value={state.first_reminder_offset}
            inputProps={{ min: 1 }}
            onChange={(e) =>
              setState((prev) => ({
                ...prev,
                first_reminder_offset: Number(e.target.value)
              }))
            }
            sx={{ mt: 2 }}
          />
          <Box className="category-title push-top" display="flex" justifyContent="space-between">
            Segundo lembrete
            <Button
              sx={{ marginBottom: "-.75rem" }}
              onClick={() => setState((prev) => ({
                ...prev,
                second_reminder_template: null
              }))}>Desativar</Button>
          </Box>
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>Template</InputLabel>
            <Select
              value={state.second_reminder_template}
              label="Template"
              onChange={(e) =>
                setState((prev) => ({
                  ...prev,
                  second_reminder_template: e.target.value
                }))
              }
              disabled={!state.origin_number}
            >
              {templates[state.origin_number]?.waba_templates?.filter(filterTemplates(3)).map((tpl) => (
                <MenuItem key={tpl.name} value={tpl.name}>
                  {tpl.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Minutos antes do evento"
            value={state.second_reminder_offset}
            inputProps={{ min: 1 }}
            onChange={(e) =>
              setState((prev) => ({
                ...prev,
                second_reminder_offset: Number(e.target.value)
              }))
            }
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button sx={{ color: "#21446C" }} onClick={handleClose}>Cancelar</Button>
          <Button sx={{ color: "#21446C" }} onClick={handleSave} disabled={loading}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
